import {Component, Input, OnInit} from '@angular/core';
import {UntypedFormArray, UntypedFormBuilder, UntypedFormGroup} from '@angular/forms';
import {IntakeFormSection, IntakeOption} from '@ee/common/models';
import {GenerateTenantForm} from '@ee/common/forms';
import {IntakeFormFields} from '@ee/common/enums';
import {EVICTION_REASONS_CLAIMS_MAP} from '@ee/common/constants';

@Component({
  selector: 'ee-eviction-tenants-section',
  template: `
    <div class="tenants-form section-wrapper" [ngClass]="{ 'demo-mode': demoMode }" [formGroup]="form">
      <div class="in-row-grid ee__standard-shadow">
        @if (tenants?.length) {
          <mat-accordion class="tenants-wrapper quad-column">
            @for (t of tenants.controls; track t; let i = $index) {
              <div class="tenants ee__standard-shadow">
                <mat-expansion-panel
                  [ngClass]="{ 'demo-mode': demoMode }"
                  [expanded]="i === tenants.controls.length - 1"
                  [disabled]="demoMode">
                  <mat-expansion-panel-header>
                    <mat-panel-title class="tenant-name">
                      @if (t.get('first_name')?.value && t.get('last_name')?.value) {
                        <span>{{ t.get('first_name')?.value }} {{ t.get('last_name')?.value }}</span>
                      } @else {
                        No name specified
                      }
                      @if (i > 0) {
                        <button
                          class="ee__button gray-bg black remove"
                          type="button"
                          mat-icon-button
                          aria-label="Remove"
                          (click)="removeTenant(i)"
                          tp="Remove tenant">
                          <mat-icon fontSet="fa" fontIcon="fa-trash"></mat-icon>
                        </button>
                      }
                    </mat-panel-title>
                  </mat-expansion-panel-header>
                  <div class="in-row-grid">
                    <mat-form-field class="double-column">
                      <mat-label>Tenant's First Name</mat-label>
                      <input matInput [formControl]="$any(t.get('first_name'))" required autocomplete="off" />
                      @if (t.get('first_name').hasError('required')) {
                        <mat-error>Please enter tenant's first name.</mat-error>
                      }
                      <mat-hint>Required. {{ tenantSectionLocalization?.['first_name_hint'] }}</mat-hint>
                    </mat-form-field>
                    @if (options[intakeFields.MIDDLE_NAME]?.show) {
                      <mat-form-field>
                        <mat-label>Tenant's Middle Name</mat-label>
                        <input matInput [formControl]="$any(t.get('middle_name'))" autocomplete="off" />
                        <mat-hint>Optional. {{ tenantSectionLocalization?.['middle_name_hint'] }}</mat-hint>
                      </mat-form-field>
                    }
                    <mat-form-field class="double-column">
                      <mat-label>Tenant's Last Name</mat-label>
                      <input matInput [formControl]="$any(t.get('last_name'))" required autocomplete="off" />
                      @if (t.get('last_name').hasError('required')) {
                        <mat-error>Please enter tenant's last name.</mat-error>
                      }
                      <mat-hint>Required. {{ tenantSectionLocalization?.['last_name_hint'] }}</mat-hint>
                    </mat-form-field>
                    @if (options[intakeFields.PHONE_NUMBER].show) {
                      <mat-form-field>
                        <mat-label>Phone Number</mat-label>
                        <input
                          matInput
                          mask="(000) 000-0000"
                          autocomplete="off"
                          [formControl]="$any(t.get('phone_number'))"
                          [required]="options[intakeFields.PHONE_NUMBER].required" />
                        @if (t.get('phone_number').hasError('required')) {
                          <mat-error>Please enter tenant's phone.</mat-error>
                        }
                        <mat-hint>{{ options[intakeFields.PHONE_NUMBER].required ? 'Required.' : 'Optional.' }} {{ tenantSectionLocalization?.['phone_number_hint'] }}</mat-hint>
                      </mat-form-field>
                    }
                    @if (options[intakeFields.SSN].show) {
                      <mat-form-field>
                        <mat-label>SSN</mat-label>
                        <input
                          matInput
                          mask="000-00-0000"
                          [formControl]="$any(t.get('ssn'))"
                          [required]="options[intakeFields.SSN].required"
                          autocomplete="off" />
                        @if (t.get('ssn').hasError('required')) {
                          <mat-error>Please enter tenant's social security number.</mat-error>
                        }
                        <mat-hint>{{ options[intakeFields.SSN].required ? 'Required.' : 'Optional.' }} {{ tenantSectionLocalization?.['ssn_enc_hint'] }}</mat-hint>
                      </mat-form-field>
                    }
                    @if (options[intakeFields.EMAIL]?.show) {
                      <mat-form-field>
                        <mat-label>Email Address</mat-label>
                        <input
                          matInput
                          [formControl]="$any(t.get('email'))"
                          [required]="options[intakeFields.EMAIL].required" />
                        @if (t.get('email').hasError('required')) {
                          <mat-error>Please enter tenant's email address.</mat-error>
                        }
                        <mat-hint>{{ options[intakeFields.EMAIL]?.required ? 'Required.' : 'Optional.' }} {{ tenantSectionLocalization?.['email_hint'] }}</mat-hint>
                      </mat-form-field>
                    }
                    @if (options[intakeFields.DOB]?.show) {
                      <mat-form-field>
                        <mat-label>Date of Birth</mat-label>
                        <input
                          matInput
                          [formControl]="$any(t.get('dob'))"
                          [matDatepicker]="dbDob"
                          [required]="options[intakeFields.DOB].required"
                          autocomplete="off" />
                        @if (t.get('dob').hasError('required')) {
                          <mat-error>Please enter tenant's date of birth.</mat-error>
                        }
                        <mat-hint>{{ options[intakeFields.DOB]?.required ? 'Required.' : 'Optional.' }} {{ tenantSectionLocalization?.['dob_hint'] }}</mat-hint>
                        <mat-datepicker-toggle matSuffix [for]="dbDob"></mat-datepicker-toggle>
                        <mat-datepicker #dbDob></mat-datepicker>
                      </mat-form-field>
                    }
                    @if (options[intakeFields.IS_NOT_ACTIVE_MILITARY_MEMBER]?.show) {
                      <div class="double-column">
                        <div class="flex flex-row justify-start items-center">
                          <mat-checkbox
                            class="mr-1"
                            [formControl]="$any(t.get('is_not_active_military_member'))"
                            [required]="options[intakeFields.IS_NOT_ACTIVE_MILITARY_MEMBER]?.required">
                            Tenant is not an active military member.
                          </mat-checkbox>
                          <a href="https://scra.dmdc.osd.mil/scra/" target="_blank" class="pt-2">
                            <mat-icon
                              fontSet="fa"
                              fontIcon="fa-info-circle"
                              tp="Click to see more information about SCRA laws."></mat-icon>
                          </a>
                        </div>
                        @if (t.get('is_not_active_military_member').hasError('requiredTrue')) {
                          <mat-error>Tenant cannot be an active member of the military to proceed.</mat-error>
                        }
                      </div>
                    }
                  </div>
                </mat-expansion-panel>
              </div>
            }
          </mat-accordion>
        } @else {
          <div class="invalid">Please add one or more tenants to continue.</div>
        }
        @if (!demoMode) {
          <button
            class="quad-column"
            mat-flat-button
            type="button"
            color="primary"
            [disabled]="form.invalid"
            (click)="addTenant()">
            Add Another Tenant
          </button>
        }
      </div>

      <hr class="mb-4 mt-4" />

      <div class="in-row-grid">
        @if (options[intakeFields.NOT_ACTIVE_MILITARY]?.show) {
          <div class="double-column">
            <div class="flex flex-row justify-start items-center">
              <mat-checkbox
                class="mr-1"
                formControlName="not_active_military"
                [required]="options[intakeFields.NOT_ACTIVE_MILITARY]?.required">
                Tenant(s) are not active military members.
              </mat-checkbox>
              <a href="https://scra.dmdc.osd.mil/scra/" target="_blank">
                <mat-icon
                  fontSet="fa"
                  fontIcon="fa-info-circle"
                  tp="Click to see more information about SCRA laws."></mat-icon>
              </a>
            </div>
            @if (form.get('not_active_military').hasError('required')) {
              <mat-error>Tenant(s) cannot be active member of the military to proceed.</mat-error>
            }
          </div>
        }

        @if (options[intakeFields.APPLIED_FOR_ASSISTANCE] && options[intakeFields.APPLIED_FOR_ASSISTANCE].show) {
          <div class="double-column">
            <div class="flex flex-row justify-start items-center">
              <mat-checkbox class="mr-1" formControlName="applied_for_assistance">
                {{
                  !!options[intakeFields.APPLIED_FOR_ASSISTANCE]['label_override']
                    ? options[intakeFields.APPLIED_FOR_ASSISTANCE]['label_override']
                    : 'Tenant(s) has applied for government assistance.'
                }}
              </mat-checkbox>
              @if (options[intakeFields.APPLIED_FOR_ASSISTANCE]['more_info_link']) {
                <a [href]="options[intakeFields.APPLIED_FOR_ASSISTANCE]['more_info_link']" target="_blank">
                  <mat-icon fontSet="fa" fontIcon="fa-info-circle" tp="Click to see more information."></mat-icon>
                </a>
              }
            </div>
            @if (form.get('applied_for_assistance').hasError('required')) {
              <mat-error>Tenant(s) must have not already filed for government assistance to proceed.</mat-error>
            }
          </div>
        }
      </div>

      <div class="in-row-grid">
        @if (options[intakeFields.EVICTION_REASON].show) {
          <mat-form-field>
            <mat-label>Eviction Reason</mat-label>
            <mat-select formControlName="eviction_reason" [required]="options[intakeFields.EVICTION_REASON].required">
              @for (reason of evictionReasons; track reason) {
                <mat-option [value]="reason">
                  {{ reason }}
                </mat-option>
              }
            </mat-select>
            @if (form?.get('eviction_reason').hasError('required')) {
              <mat-error>Please select a reason.</mat-error>
            }
            <mat-hint>
              {{ options[intakeFields.EVICTION_REASON].required ? 'Required.' : 'Optional.' }} {{ tenantSectionLocalization?.['eviction_reason_hint'] }}
            </mat-hint>
          </mat-form-field>
        }
        @if (options[intakeFields.REASON].show) {
          <mat-form-field class="triple-column">
            <mat-label>Other Reason Description</mat-label>
            <input matInput formControlName="reason" [required]="options[intakeFields.REASON].required" />
            @if (form.get('reason').hasError('required')) {
              <mat-error>An eviction reason is required.</mat-error>
            }
            <mat-hint>
              {{ options[intakeFields.REASON].required ? 'Required.' : 'Optional.' }} {{ tenantSectionLocalization?.['reason_hint'] }}
            </mat-hint>
          </mat-form-field>
        }
      </div>

      @if (tenantSection?.fields?.length && tenantFields) {
        <div class="in-row-grid" formGroupName="custom_fields">
          @for (f of tenantSection?.fields; track f; let i = $index) {
            <ee-field
              [ngClass]="{
                'margin-bottom-sm': i < tenantSection.fields.length - 1,
                'md:mb-4': i < tenantSection.fields.length - 1,
                'double-column': f.columns === 2,
                'triple-column': f.columns === 3,
                'quad-column': f.columns === 4
              }"
              [hideFromIntake]="f.hide_from_intake"
              [hidden]="f.hide_from_intake && !overrideFieldVisibility"
              [control]="$any(tenantFields.controls[f.field])"
              [compact]="false"
              [field]="f"></ee-field>
          }
        </div>
      }
    </div>
  `,
  styles: [
    `
      .tenant-name {
        font-size: 1.1rem;
        justify-content: space-between;
        align-items: center;
      }

      .tenants-form {
        &.demo-mode {
          .tenants {
            padding-right: 0;
          }
        }
      }
      .invalid {
        color: red;
      }
      mat-mdc-accordion.tenants-wrapper {
        .mat-mdc-expansion-panel-header-title {
          font-size: 1rem;
          font-weight: 500;
        }
      }
    `
  ],
  standalone: false
})
export class EvictionTenantsSectionComponent implements OnInit {
  @Input() public form: UntypedFormGroup;

  @Input() public options: Map<string, IntakeOption> | undefined;

  @Input() public demoMode = false;

  @Input() public tenantSectionLocalization: { [key: string]: string | undefined } = {};

  @Input() overrideFieldVisibility = false;

  @Input({ required: true }) public tenantSection: IntakeFormSection;

  intakeFields = IntakeFormFields;

  constructor(private fb: UntypedFormBuilder) {}

  ngOnInit() {
    if (!this.tenants.length) {
      this.addTenant();
    }
  }

  public get evictionReasons(): string[] {
    let options = Array.from(EVICTION_REASONS_CLAIMS_MAP.keys());
    if (this.options && this.options[this.intakeFields.EVICTION_REASON]?.options?.length) {
      options = this.options[this.intakeFields.EVICTION_REASON].options;
    }
    return options;
  }

  get tenantFields(): UntypedFormGroup | undefined {
    return this.form.get('custom_fields') as UntypedFormGroup;
  }

  addTenant() {
    const newTenant = GenerateTenantForm(this.fb, this.options, this.demoMode);
    if (this.demoMode) {
      newTenant.get('first_name').setValue('Demo');
      newTenant.get('last_name').setValue('Tenant');
    }
    this.tenants.push(newTenant, { emitEvent: false });
  }

  removeTenant(i: number) {
    this.tenants.removeAt(i);
  }

  get tenants(): UntypedFormArray {
    return this.form.get('tenants') as UntypedFormArray;
  }
}
